import { useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet, FileDown, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useApi, downloadFile, errMsg } from "../lib/api";
import { PageHead, Card, Field, Input, Select, Btn, Loading, Empty, Overline } from "../components/kit";
import { todayWIB, num, CATEGORY_LABEL } from "../lib/constants";

const KINDS = [["position", "Posisi Stok Saat Ini"], ["recap", "Rekap Mutasi per Periode"]];
const POS_COLS = ["Kategori", "Barang", "Satuan", "Stok", "Minimum", "Status"];
const RECAP_COLS = ["Kategori", "Barang", "Stok Awal", "Masuk", "Keluar", "Produksi", "Reject", "Opname", "Stok Akhir"];
const Q = (v, tone = "") => <span className={`font-mono ${tone}`}>{num(v)}</span>;

function Table({ cols, rows, testid }) {
  if (!rows.length) return <Empty text="Tidak ada data." data-testid={`${testid}-empty`} />;
  return (
    <div className="overflow-x-auto bg-white rounded-2xl border border-slate-200" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-slate-900 text-white"><tr>{cols.map((c) => <th key={c} className="px-3 py-3 text-left font-bold whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>{rows.map(([key, ...r]) => <tr key={key} className="border-t border-slate-100 even:bg-slate-50">{r.map((v, j) => <td key={cols[j]} className="px-3 py-2.5 whitespace-nowrap">{v}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

const itemLink = (r) => <Link to={`/stok/${r.id}`} className="font-bold text-sky-600" data-testid={`sr-item-${r.id}`}>{r.name}</Link>;
const posRow = (r) => [r.id, r.category, itemLink(r), r.unit, Q(r.stock, r.low ? "font-bold text-rose-600" : "font-bold"), Q(r.min_stock),
  <span key="s" className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${r.low ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{r.status}</span>];
const recapRow = (r) => [r.id, r.category, <span key="n">{itemLink(r)} <span className="text-slate-400 text-xs">{r.unit}</span></span>, Q(r.opening),
  Q(r.in, "text-emerald-700"), Q(r.out, "text-rose-600"), Q(r.production, "text-rose-600"), Q(r.reject, "text-rose-600"),
  <span key="o" className="font-mono">{r.opname > 0 ? "+" : ""}{num(r.opname)}</span>, Q(r.closing, "font-bold")];

export default function StockReport() {
  const today = todayWIB();
  const [kind, setKind] = useState("position");
  const [cat, setCat] = useState("");
  const [range, setRange] = useState({ start: today.slice(0, 8) + "01", end: today });
  const [busy, setBusy] = useState("");
  const qs = `category=${cat}&start=${range.start}&end=${range.end}`;
  const { data } = useApi(`/stock-report/${kind}?${qs}`, 0);
  const exp = async (fmt) => {
    setBusy(fmt);
    try { await downloadFile(`/stock-report/export?kind=${kind}&format=${fmt}&${qs}`, `laporan_stok_${kind}.${fmt}`); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(""); }
  };
  return (
    <>
      <Link to="/stok" className="inline-flex items-center gap-1 text-slate-600 font-semibold h-11" data-testid="back-to-stock"><ArrowLeft className="w-5 h-5" /> Stok Barang</Link>
      <PageHead title="Laporan Stok" sub="Posisi stok & rekap mutasi per barang" />
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="sr-kinds">
        {KINDS.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`sr-kind-${k}`}
            className={`shrink-0 h-11 px-4 rounded-full font-bold text-sm border ${kind === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>{l}</button>
        ))}
      </div>
      <Card className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <Field label="Kategori" className={kind === "recap" ? "" : "col-span-2 md:col-span-3"}><Select placeholder="Semua kategori" options={Object.entries(CATEGORY_LABEL)} value={cat} onChange={(e) => setCat(e.target.value)} data-testid="sr-category-select" /></Field>
        {kind === "recap" && <>
          <Field label="Dari"><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="sr-start-input" /></Field>
          <Field label="Sampai"><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="sr-end-input" /></Field>
        </>}
        <Btn variant="success" loading={busy === "xlsx"} onClick={() => exp("xlsx")} data-testid="sr-export-excel-btn"><FileSpreadsheet className="w-5 h-5" /> Excel</Btn>
        <Btn variant="danger" loading={busy === "pdf"} onClick={() => exp("pdf")} data-testid="sr-export-pdf-btn"><FileDown className="w-5 h-5" /> PDF</Btn>
      </Card>
      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sr-summary">
            {Object.entries(data.summary).map(([k, v]) => <Card key={k} className="!p-4"><Overline>{k}</Overline><div className="mt-1 font-mono font-extrabold text-lg">{v}</div></Card>)}
          </div>
          {kind === "position"
            ? <Table testid="sr-position-table" cols={POS_COLS} rows={data.rows.map(posRow)} />
            : <Table testid="sr-recap-table" cols={RECAP_COLS} rows={data.rows.map(recapRow)} />}
        </>
      )}
    </>
  );
}
